<template>
  <v-app>
    <app-layout v-if="isLoggedIn"></app-layout>
    <login-page v-else></login-page>

    <!-- refreshLoginDialog -->
    <v-row align="center" justify="center">
      <v-dialog v-model="refreshLoginDialogShowed" persistent max-width="600">
        <v-card class="elevation-12">
          <v-toolbar color="primary" dark flat>
            <v-toolbar-title>{{ $t('expiredLogin') }}</v-toolbar-title>
          </v-toolbar>
          <v-card-text>
            <v-form ref="form" v-model="loginDialogIsValid" @keyup.native.enter="refreshLogin">
              <v-text-field
                v-model="refreshLoginUsername"
                :label="$t('username')"
                prepend-icon="mdi-account"
                type="text"
                readonly
              />
              <v-text-field
                v-model="refreshLoginPassword"
                :label="$t('password')"
                prepend-icon="mdi-lock"
                type="password"
                :rules="[rules.required]"
              />
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn @click="logout">
              <v-icon>mdi-logout</v-icon>
              {{ $t('logout') }}
            </v-btn>
            <v-btn
              :disabled="!loginDialogIsValid"
              color="primary"
              @click="refreshLogin"
            >
              {{ $t('login') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-row>

    <!-- activateLoggerDialog -->
    <v-row justify="center">
      <v-dialog v-model="activateLoggerDialogShowed" persistent max-width="600">
        <v-card>
          <v-card-title>
            <span class="headline">{{ $t('activateLogger') }}</span>
          </v-card-title>
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="4" class="font-weight-bold">
                  {{ $t('name') }}:
                </v-col>
                <v-col cols="8">
                  {{ loggerKeyStoreName }}
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="4" class="font-weight-bold">
                  {{ $t('status') }}:
                </v-col>
                <v-col cols="8">
                  {{ activateLoggerStatus }}
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-text-field v-model="activateLoggerPassword" :label="$t('password')" type="password" required></v-text-field>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="activateLogger">{{ $t('activate') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-row>

    <!-- confirmationDialog -->
    <v-row justify="center">
      <v-dialog v-model="confirmationDialogShowed" persistent max-width="600">
        <v-card id="1d52ec91">
          <v-card-title class="headline yellow darken-3">
            <v-icon id="66dbc1b0" left large>mdi-alert-circle</v-icon>
            {{ confirmationHeadline }}
          </v-card-title>
          <v-card-text class="subtitle-1 pt-3">{{ confirmationSubTitle }}</v-card-text>
          <v-card-text v-if="confirmationDialogCheckBoxChecked">
            <v-checkbox id="3dfe686c" v-model="confirmationCheckbox" class="subtitle-2 pt-3" :label="confirmationCheckboxLabel"></v-checkbox>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-3" text @click="confirmationAccept(false, false)">{{ $t('no') }}</v-btn>
            <v-btn id="ac8bd4aa" class="primary" @click="confirmationAccept(true, confirmationCheckbox)">{{ $t('yes') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-row>

    <!-- snackbar -->
    <v-snackbar id="c9a32ec0" v-model="toastShowed" top :color="toastColor">
      <v-icon id="cb483516" dark left large>{{ toastIcon }}</v-icon>
      <div class="d-flex flex-column">
        <div id="bd56b14b">{{ toastMessage }}</div>
        <div v-if="toastSecondMessageShowed" id="31f2417b">{{ toastSecondMessage }}</div>
      </div>
      <v-btn id="a4cb7d4a" text @click="toastShowed = false">
        {{ $t('close') }}
      </v-btn>
    </v-snackbar>
  </v-app>
</template>

<script>
import LoginPage from '@/views/Login'
import AppLayout from '@/views/AppLayout'
import eventBus from './event-bus'

export default {
  name: 'App',

  components: {
    LoginPage,
    AppLayout
  },

  data() {
    return {
      loginDialogIsValid: true,
      refreshLoginDialogShowed: false,
      refreshLoginUsername: null,
      refreshLoginPassword: null,
      activateLoggerDialogShowed: false,
      activateLoggerInfo: null,
      activateLoggerPassword: null,
      toastShowed: false,
      toastIcon: null,
      toastColor: null,
      toastMessage: null,
      toastSecondMessageShowed: false,
      toastSecondMessage: null,
      confirmationDialogShowed: false,
      confirmationCheckbox: true,
      confirmationDialogCheckBoxChecked: false,
      confirmationHeadline: null,
      confirmationSubTitle: null,
      confirmationCheckboxLabel: null,
      rules: {
        required: v => !!v || this.$t('validation.requiredField')
      }
    }
  },

  computed: {
    isLoggedIn() {
      return !!this.$store.state.currentUser
    },
    activateLoggerStatus() {
      if (this.activateLoggerInfo == null) return '-'
      return this.activateLoggerInfo.failed
        ? this.$t('loggerKeystoreFailed')
        : this.$t('loggerKeystoreNeedsActivation')
    },
    loggerKeyStoreName() {
      return this.activateLoggerInfo == null
        ? '-'
        : this.activateLoggerInfo.keyStoreName
    }
  },

  mounted() {
    eventBus.$on('showRefreshLogin', ({ username }) => {
      this.refreshLoginDialogShowed = true
      this.refreshLoginUsername = username
      this.refreshLoginPassword = null
    })

    eventBus.$on('showActivateLogger', ({ loggerInfo }) => {
      this.activateLoggerDialogShowed = true
      this.activateLoggerInfo = loggerInfo
      this.refreshLoginPassword = null
    })

    eventBus.$on('showToast', ({ icon, type, message }) => {
      this.toastShowed = true
      this.toastIcon = icon
      this.toastColor = type
      this.toastMessage = message
      this.toastSecondMessageShowed = false
      this.toastSecondMessage = null
    })

    eventBus.$on('showExpandedToast', ({ icon, type, message, message2 }) => {
      this.toastShowed = true
      this.toastIcon = icon
      this.toastColor = type
      this.toastMessage = message
      this.toastSecondMessageShowed = true
      this.toastSecondMessage = message2
    })

    eventBus.$on('showConfirmationDialog', ({ title, text, checkBoxChecked, checkBoxLabel }) => {
      this.confirmationDialogShowed = true
      this.confirmationCheckbox = true
      this.confirmationDialogCheckBoxChecked = checkBoxChecked
      this.confirmationHeadline = title
      this.confirmationSubTitle = text
      this.confirmationCheckboxLabel = checkBoxLabel
    })
  },

  methods: {
    refreshLogin() {
      eventBus.$emit('refreshLogin', { password: this.refreshLoginPassword })
      this.refreshLoginDialogShowed = false
    },

    activateLogger() {
      eventBus.$emit('activateLogger', { password: this.activateLoggerPassword })
      this.activateLoggerPassword = null
      this.activateLoggerDialogShowed = false
    },

    logout() {
      this.$store.commit('removeCurrentUser')
      this.$router.push({ name: 'Login' })
      this.refreshLoginDialogShowed = false
    },

    confirmationAccept(param, param2) {
      eventBus.$emit('confirmationAccept', { param, param2 })
      this.confirmationDialogShowed = false
    }

  }
}
</script>
